import { useTranslation } from "react-i18next";
import React, { useMemo, useState } from "react";
import StatusBadge from "../../../../components/StatusBadge";
import { useQuery, useQueryClient, useMutation } from "react-query";
import { apollo } from "../../../../utils/apollo";
import {
  GET_COC_IMPORT_LIST,
  GET_FILE_IMPORT_LIST,
  GENERATE_COC_CSV,
  RECHECK_COC_IMPORT_MEMBER,
  RESYNC_ALL_FAILED_COC_MEMBERS,
} from "../../../../utils/graphql";
import PaginationFull from "../../../../components/PaginationFull";
import TextInput from "../../../../components/TextInput";
import Button from "../../../../components/Button";
import IconButton from "../../../../components/IconButton";
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  ServerIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { EllipsisVerticalIcon } from "@heroicons/react/20/solid";
import { toast } from "react-toastify";
import { cn } from "../../../../utils/style";

// ──────────────────────────────────────────────────────────────────────────────
// Download helper
// ──────────────────────────────────────────────────────────────────────────────
function triggerDownload(url, filename) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

// ──────────────────────────────────────────────────────────────────────────────
// Stat card
// ──────────────────────────────────────────────────────────────────────────────
function StatCard({ label, value, variant = "slate" }) {
  const variants = {
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    rose: "bg-rose-50 border-rose-200 text-rose-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
  };
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center rounded-2xl border p-5 gap-1 transition-all",
        variants[variant],
      )}
    >
      <span className="text-3xl font-black">{value ?? "—"}</span>
      <span className="text-[10px] font-black uppercase tracking-widest opacity-70">
        {label}
      </span>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────────────────────────────────────
function COCImportResultsView({ fileImport, onBack }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [submitAtOnce, setSubmitAtOnce] = useState(false);
  const fileImportUuid = fileImport?.fileImportUuid;

  const batch = fileImport;
  const isProcessing =
    batch?.status === "processing" || batch?.status === "pending";
  const progressPct = batch?.totalRecords
    ? Math.min(
      100,
      Math.max(5, (batch.importedRecords / batch.totalRecords) * 100),
    )
    : 0;

  // ── Member list ───────────────────────────────────────────────────────────
  const memberQuery = useQuery({
    queryKey: [
      "coc_import_results_members",
      { page, fileImportUuid, searchTerm, status: batch?.status, imported: batch?.importedRecords },
    ],
    queryFn: async () => {
      const { data } = await apollo.query({
        query: GET_COC_IMPORT_LIST,
        variables: {
          page,
          first: 15,
          filter: {
            fileUuid: fileImportUuid || null,
            searchTerm: searchTerm || null,
          },
        },
      });
      return data?.getCOCImportList;
    },
    enabled: !!fileImportUuid,
    keepPreviousData: true,
  });

  const members = useMemo(
    () => (memberQuery.isSuccess ? memberQuery.data : null),
    [memberQuery],
  );

  // ── CSV Download ──────────────────────────────────────────────────────────
  const downloadMutation = useMutation({
    mutationFn: async ({ type }) => {
      const { data } = await apollo.mutate({
        mutation: GENERATE_COC_CSV,
        variables: { fileImportUuid, type },
      });
      return data?.generateCocCsv;
    },
    onSuccess: (result, { type }) => {
      if (result?.success && result?.csvFile) {
        toast.success(t("Download started."));
        const labels = { all: "all", synced: "synced", errors: "errors" };
        triggerDownload(
          result.csvFile,
          `coc_${fileImportUuid}_${labels[type] ?? type}.csv`,
        );
      } else {
        toast.error(result?.message || t("Failed to generate CSV."));
      }
    },
    onError: () => toast.error(t("Failed to generate CSV.")),
  });

  const handleDownload = (type) => {
    if (downloadMutation.isLoading) return;
    downloadMutation.mutate({ type });
  };

  // ── Re-sync All ───────────────────────────────────────────────────────────
  const resyncAllMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apollo.mutate({
        mutation: RESYNC_ALL_FAILED_COC_MEMBERS,
        variables: { fileImportUuid, submitAtOnce },
      });
      return data?.reSyncAllFailedCocMembers;
    },
    onSuccess: (success) => {
      if (success) {
        toast.success(t("Bulk re-sync operation initiated."));
        queryClient.invalidateQueries(["coc_import_batch_status", fileImportUuid]);
        queryClient.invalidateQueries(["coc_import_results_members"]);
      } else {
        toast.error(t("Failed to initiate bulk re-sync."));
      }
    },
    onError: (error) => {
      const msg = error?.graphQLErrors?.[0]?.message || error?.message || t("An error occurred during bulk re-sync.");
      toast.error(msg);
    },
  });

  return (
    <div className="mt-6 space-y-6 animate-in fade-in slide-in-from-top-2 duration-300">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            {t("Back to list")}
          </button>
          <span className="text-slate-300">|</span>
          <h2 className="text-sm font-black uppercase tracking-widest text-slate-700">
            {t("Import Results")}
          </h2>
          <span className="text-xs font-mono text-slate-400">
            {fileImportUuid}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Re-sync All Options */}
          {batch?.failedRecords > 0 && (
            <div className="flex items-center gap-2 p-1 bg-indigo-50/50 rounded-2xl border border-indigo-100 shadow-sm">
              <button
                onClick={() => setSubmitAtOnce(!submitAtOnce)}
                className={cn(
                  "flex items-center gap-2 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all",
                  submitAtOnce
                    ? "bg-indigo-600 text-white shadow-md shadow-indigo-200"
                    : "text-indigo-600 hover:bg-indigo-100"
                )}
              >
                {submitAtOnce ? <ServerIcon className="h-3 w-3" /> : <UserIcon className="h-3 w-3" />}
                {submitAtOnce ? t("Consolidated") : t("Individual")}
              </button>

              <button
                onClick={() => resyncAllMutation.mutate()}
                disabled={resyncAllMutation.isLoading || isProcessing}
                className={cn(
                  "flex items-center gap-2 px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-600 hover:text-white hover:border-indigo-600 transition-all active:scale-[0.95] disabled:opacity-50 disabled:cursor-not-allowed shadow-sm",
                  resyncAllMutation.isLoading && "animate-pulse"
                )}
              >
                <ArrowPathIcon className={cn("h-3.5 w-3.5", resyncAllMutation.isLoading && "animate-spin")} strokeWidth={3} />
                {t("Re-sync All Errors")}
              </button>
            </div>
          )}

          {/* Download buttons */}
          <div className="flex items-center gap-2 pe-1.5 ps-4 py-1.5 bg-slate-50/80 backdrop-blur-md rounded-full border border-slate-200 transition-all shadow-sm hover:shadow-md">
            <span className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-400 mr-1">
              {t("Export")}
            </span>
            <button
              onClick={() => handleDownload("all")}
              disabled={downloadMutation.isLoading}
              className="flex items-center gap-1.5 px-4 py-2 text-[11px] font-black uppercase tracking-wider text-slate-600 bg-white border border-slate-200/80 rounded-full hover:bg-slate-100 hover:text-slate-900 transition-all shadow-sm active:scale-[0.95]"
            >
              <ArrowDownTrayIcon className="w-3.5 h-3.5" strokeWidth={2.5} />
              {t("All")}
            </button>

            <button
              onClick={() => handleDownload("synced")}
              disabled={downloadMutation.isLoading}
              className="flex items-center gap-1.5 px-4 py-2 text-[11px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200/80 rounded-full hover:bg-emerald-100 hover:text-emerald-900 transition-all shadow-[0_2px_8px_-3px_rgba(16,185,129,0.3)] hover:shadow-[0_4px_12px_-2px_rgba(16,185,129,0.4)] active:scale-[0.95]"
            >
              <ArrowDownTrayIcon className="w-3.5 h-3.5" strokeWidth={2.5} />
              {t("Synced")}
            </button>

            <button
              onClick={() => handleDownload("errors")}
              disabled={downloadMutation.isLoading}
              className="flex items-center gap-1.5 px-4 py-2 text-[11px] font-black uppercase tracking-wider text-rose-700 bg-rose-50 border border-rose-200/80 rounded-full hover:bg-rose-100 hover:text-rose-900 transition-all shadow-[0_2px_8px_-3px_rgba(244,63,94,0.3)] hover:shadow-[0_4px_12px_-2px_rgba(244,63,94,0.4)] active:scale-[0.95]"
            >
              <ArrowDownTrayIcon className="w-3.5 h-3.5" strokeWidth={2.5} />
              {t("Errors")}
            </button>
          </div>
        </div>
      </div>

      {/* ── Stats + progress ─────────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200/60 rounded-2xl p-6 shadow-sm space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label={t("Total")}
            value={batch?.totalRecords}
            variant="blue"
          />
          <StatCard
            label={t("Synced")}
            value={batch?.importedRecords}
            variant="emerald"
          />
          <StatCard
            label={t("Errors")}
            value={batch?.failedRecords}
            variant="rose"
          />
          <StatCard
            label={t("Status")}
            value={
              <span className="capitalize text-xl font-black">
                {batch?.status}
              </span>
            }
            variant={
              batch?.status === "completed"
                ? "emerald"
                : batch?.status === "failed"
                  ? "rose"
                  : "slate"
            }
          />
        </div>

        {isProcessing && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
              <span className="flex items-center gap-1.5">
                <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                {t("Processing...")}
              </span>
              <span>{Math.round(progressPct)}%</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden border border-slate-200">
              <div
                className="bg-primary-500 h-2 rounded-full transition-all duration-700 shadow-[0_0_8px_rgba(59,130,246,0.4)]"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Search ───────────────────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200/60 rounded-2xl p-4 shadow-sm">
        <div className="max-w-sm">
          <TextInput
            name="results-search"
            label={t("Search")}
            placeholder={t("Emirates ID, Shafafiya Ref, or UID...")}
            id="results-search"
            get={searchTerm}
            set={setSearchTerm}
          />
        </div>
      </div>

      {/* ── Member table ─────────────────────────────────────────────────── */}
      <div className="flow-root">
        <div className="overflow-auto pb-16 rounded-2xl border border-slate-200/60 bg-white shadow-sm">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50/50">
                <tr>
                  {[
                    "",
                    "Status",
                    "Emirates ID",
                    "UID",
                    "Shafafiya Ref No",
                    "Penalty",
                    "No Insurance Days",
                    "Validity Date",
                    "Error Code",
                  ].map((h) => (
                    <th
                      key={h}
                      scope="col"
                      className="px-3 py-2.5 text-start text-[11px] font-black uppercase tracking-wider text-slate-500 whitespace-nowrap"
                    >
                      {h ? t(h) : ""}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {members?.data?.map((m, idx) => {
                  const hasError = !!(m.errorCode && m.errorCode.trim());
                  return (
                    <tr
                      key={m.eid + idx}
                      className="hover:bg-slate-50/50 transition-colors"
                    >
                      {/* Actions column moved to the start */}
                      <td className="whitespace-nowrap px-3 py-2 text-sm text-slate-500">
                        <MemberActions record={m} />
                      </td>

                      {/* Status badge */}
                      <td className="whitespace-nowrap px-3 py-2 text-sm">
                        <StatusBadge status={m.integrationStatus} />
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-sm font-bold text-slate-900 font-mono tracking-tight">
                        {m.eid}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-sm font-bold text-slate-900 font-mono tracking-tight opacity-60">
                        {m.uid}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-sm text-slate-500 font-mono text-xs">
                        {m.shafafiyaReferenceNo || "—"}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-sm text-slate-500">
                        {m.penalty != null ? `${m.penalty} ${t("AED")}` : "—"}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-sm text-slate-500">
                        {m.nonInsuranceDays ?? "—"}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-sm text-slate-500">
                        {m.validityDate ? m.validityDate.split(" ")[0] : "—"}
                      </td>

                      <td className="px-3 py-2 text-sm min-w-[200px] max-w-md">
                        {m.errorCode ? (
                          <span className="text-rose-600 font-mono text-xs bg-rose-50 px-2 py-0.5 rounded-md border border-rose-100 break-words block">
                            {m.errorCode}
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {members?.data && !members.data.length && (
              <div className="flex items-center justify-center h-40 text-slate-400 font-light text-lg">
                {isProcessing
                  ? t("Processing members, results will appear shortly...")
                  : t("No members found.")}
              </div>
            )}
            {!members && memberQuery.isLoading && (
              <div className="flex items-center justify-center h-40 text-slate-400 font-light text-lg">
                {t("Loading...")}
              </div>
            )}
          </div>
        </div>
      </div>

      <PaginationFull
        paginator={members?.paginatorInfo}
        get={page}
        set={setPage}
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Per-member actions (Re-check)
// ──────────────────────────────────────────────────────────────────────────────
function MemberActions({ record }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const reCheckMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apollo.mutate({
        mutation: RECHECK_COC_IMPORT_MEMBER,
        variables: { eid: record?.eid },
      });
      return data?.reCheckCocImportMember;
    },
    onError: (error) => {
      // Show exact Shafafiya error from backend exception
      const msg = error?.graphQLErrors?.[0]?.message || error?.message || t("Re-sync operation failed.");
      toast.error(msg);
    },
    onSuccess: (data) => {
      if (data?.success) {
        toast.success(t("Re-sync operation completed."));
      } else {
        toast.error(data?.message || t("Re-sync operation failed."));
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(["coc_import_results_members"]);
    },
  });

  return (
    <button
      onClick={() => reCheckMutation.mutate()}
      disabled={reCheckMutation.isLoading}
      className={cn(
        "flex flex-nowrap whitespace-nowrap w-fit items-center justify-start gap-2 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-indigo-600 hover:text-white hover:bg-indigo-600 bg-indigo-50/50 rounded-lg transition-all active:scale-[0.95] border border-indigo-100/50",
        reCheckMutation.isLoading && "opacity-50 cursor-not-allowed"
      )}
    >
      <ArrowPathIcon className={cn("h-3.5 w-3.5", reCheckMutation.isLoading && "animate-spin")} />
      <span>{t("Re-sync")}</span>
    </button>
  );
}

export default COCImportResultsView;
